import { format } from "date-fns";
import { useAtom } from "jotai";
import { LayoutPanelTop } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import {
  panelCategoryFilterAtom,
  panelFiltersAtom,
  panelLicenseFilterAtom,
  panelPageAtom,
  panelSearchAtom,
  panelStatusFilterAtom,
} from "../atoms/panelsAtoms";
import { usePanelsList } from "../hooks/usePanels";
import type {
  PanelCategory,
  PanelLicense,
  PanelModerationStatus,
} from "../types";
import PanelStatusBadge from "./PanelStatusBadge";
import PanelThumbnail from "./PanelThumbnail";

const formatPrice = (priceArs?: number | null) => {
  if (priceArs == null || priceArs === 0) return "—";
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(priceArs);
};

const PanelsList = () => {
  const { t } = useTranslation("common");
  const [statusFilter, setStatusFilter] = useAtom(panelStatusFilterAtom);
  const [categoryFilter, setCategoryFilter] = useAtom(panelCategoryFilterAtom);
  const [licenseFilter, setLicenseFilter] = useAtom(panelLicenseFilterAtom);
  const [search, setSearch] = useAtom(panelSearchAtom);
  const [page, setPage] = useAtom(panelPageAtom);
  const [filters] = useAtom(panelFiltersAtom);

  const { data, isLoading, isError, error } = usePanelsList(filters);

  const statusTabs: { value: PanelModerationStatus; label: string }[] = [
    { value: "pending", label: t("PANELS.statusPending", "Pending") },
    { value: "approved", label: t("PANELS.statusApproved", "Approved") },
    { value: "rejected", label: t("PANELS.statusRejected", "Rejected") },
  ];

  const categoryOptions: { value: PanelCategory | "all"; label: string }[] = [
    { value: "all", label: t("PANELS.allCategories", "All categories") },
    {
      value: "lower_third",
      label: t("PANELS.categoryLowerThird", "Lower Third"),
    },
    { value: "label", label: t("PANELS.categoryLabel", "Label") },
    { value: "stats", label: t("PANELS.categoryStats", "Stats") },
  ];

  const licenseOptions: { value: PanelLicense | "all"; label: string }[] = [
    { value: "all", label: t("PANELS.allLicenses", "All licenses") },
    { value: "free", label: t("PANELS.licenseFree", "Free") },
    { value: "paid", label: t("PANELS.licensePaid", "Paid") },
    { value: "community", label: t("PANELS.licenseCommunity", "Community") },
  ];

  const handleStatusChange = (status: PanelModerationStatus) => {
    setStatusFilter(status);
    setPage(1);
  };

  const panels = data?.items ?? [];
  const total = data?.total ?? 0;
  const limit = filters.limit ?? 20;
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("PANELS.title", "Panels")}
        subtitle={t(
          "PANELS.subtitle",
          "Moderation queue for panels submitted to the Store.",
        )}
      />

      <Card className="border-neutral-800 bg-neutral-900/50">
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-2">
              {statusTabs.map((tab) => (
                <Button
                  key={tab.value}
                  variant={statusFilter === tab.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleStatusChange(tab.value)}
                  className={
                    statusFilter === tab.value
                      ? "bg-purple-600 text-white hover:bg-purple-700"
                      : "border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  }
                >
                  {tab.label}
                </Button>
              ))}
            </div>

            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value as PanelCategory | "all");
                setPage(1);
              }}
              className="h-9 rounded-md border border-neutral-700 bg-neutral-800/50 px-3 text-sm text-neutral-200"
            >
              {categoryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              value={licenseFilter}
              onChange={(e) => {
                setLicenseFilter(e.target.value as PanelLicense | "all");
                setPage(1);
              }}
              className="h-9 rounded-md border border-neutral-700 bg-neutral-800/50 px-3 text-sm text-neutral-200"
            >
              {licenseOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <div className="flex-1 min-w-[200px]">
              <Input
                type="text"
                placeholder={t(
                  "PANELS.searchPlaceholder",
                  "Search by name or author...",
                )}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="py-12">
              <LoadingOverlay />
            </div>
          ) : isError ? (
            <EmptyState
              title={t("PANELS.errorTitle", "Could not load panels")}
              description={
                error instanceof Error
                  ? error.message
                  : t("PANELS.errorDescription", "Try again in a moment.")
              }
            />
          ) : panels.length === 0 ? (
            <EmptyState
              icon={<LayoutPanelTop className="h-8 w-8" />}
              title={t("PANELS.emptyTitle", "No panels in this view")}
              description={t(
                "PANELS.emptyDescription",
                "There are no panels matching the current filters.",
              )}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800 hover:bg-transparent">
                    <TableHead className="text-neutral-400">
                      {t("PANELS.colPreview", "Preview")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("PANELS.colName", "Name")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("PANELS.colAuthor", "Author")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("PANELS.colCategory", "Category")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("PANELS.colLicense", "License")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("PANELS.colPrice", "Price")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("PANELS.colStatus", "Status")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("PANELS.colSubmitted", "Submitted")}
                    </TableHead>
                    <TableHead className="text-right text-neutral-400">
                      {t("PANELS.colActions", "Actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {panels.map((panel) => (
                    <TableRow
                      key={panel.id}
                      className="border-neutral-800 hover:bg-neutral-800/50"
                    >
                      <TableCell>
                        <PanelThumbnail
                          url={panel.previewThumbnailUrl}
                          alt={panel.name}
                        />
                      </TableCell>
                      <TableCell className="text-neutral-200">
                        <div className="flex flex-col">
                          <span className="font-medium">{panel.name}</span>
                          <span className="text-xs text-neutral-500">
                            v{panel.version}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-neutral-300">
                        {panel.authorName}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="border-neutral-600 text-neutral-300"
                        >
                          {panel.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-neutral-300">
                        {panel.license}
                      </TableCell>
                      <TableCell className="text-neutral-300">
                        {formatPrice(panel.priceArs)}
                      </TableCell>
                      <TableCell>
                        <PanelStatusBadge status={panel.status} />
                      </TableCell>
                      <TableCell className="text-neutral-400 text-sm">
                        {format(new Date(panel.createdAt), "dd MMM yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          asChild
                          variant="ghost"
                          size="sm"
                          className="text-purple-400 hover:bg-purple-500/10 hover:text-purple-300"
                        >
                          <Link to={`/panels/${panel.slug}`}>
                            {t("PANELS.review", "Review")}
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-neutral-800 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  >
                    {t("PANELS.prev", "Previous")}
                  </Button>
                  <span className="text-sm text-neutral-400">
                    {t("PANELS.pageOf", "Page")} {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  >
                    {t("PANELS.next", "Next")}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PanelsList;
